'use client'
import { PrismicRichText } from "@prismicio/react";
import { isFilled } from "@prismicio/client";
import { useState } from "react";

/**
 * @typedef {import("@prismicio/client").Content.NewsletterSignupSlice} NewsletterSignupSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<NewsletterSignupSlice>} NewsletterSignupProps
 * @type {import("react").FC<NewsletterSignupProps>}
 */
const NewsletterSignup = ({ slice }) => {
  const { title, description, input_placeholder, button_text } = slice.primary;
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    console.log(e);
    e.preventDefault();
    setIsLoading(true);
    setMessage('');

    try {
      const response = await fetch('/api/newsletter', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage(data.message);
        setEmail('');
      } else {
        setMessage(data.error || 'Une erreur est survenue');
      }
    } catch (error) {
      setMessage('Erreur de connexion. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="py-16 px-4 md:px-12 bg-white"
    >
      <div className="max-w-xl mx-auto text-center">
        {isFilled.richText(title) && (
          <PrismicRichText
            field={title}
            components={{
              heading1: ({ children }) => (
                <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">{children}</h2>
              ),
            }}
          />
        )}
        {isFilled.richText(description) && (
          <PrismicRichText
            field={description}
            components={{
              paragraph: ({ children }) => (
                <p className="mb-8 text-lg md:text-xl text-gray-700">{children}</p>
              ),
            }}
          />
        )}
        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4 justify-center items-center mt-6">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={input_placeholder || "Votre email"}
            className="flex-1 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500 text-gray-900 bg-gray-50"
            disabled={isLoading}
          />
          <button
            type="submit"
            disabled={isLoading || !email}
            className="px-6 py-3 rounded-lg bg-purple-600 text-white font-semibold hover:bg-purple-700 transition-colors shadow-md focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'En cours...' : (button_text || "S'inscrire")}
          </button>
        </form>
        {message && (
          <div className={`mt-4 p-3 rounded-lg text-sm ${message.includes('réussi') || message.includes('inscrit') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
            {message}
          </div>
        )}
      </div>
    </section>
  );
};

export default NewsletterSignup;
